import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type {
  AssistantChatResult,
  AssistantConfirmResult,
  AssistantSettings,
  AssistantUsageStatus,
} from "./types";

/** POST /api/assistant/chat — one turn of the assistant. */
export function useAssistantChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { message: string; conversationId?: string }) =>
      httpClient<AssistantChatResult>("/api/assistant/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    // Spend changed — refresh the usage widget.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant", "usage"] }),
  });
}

/** POST /api/assistant/confirm — execute a write the chat turn proposed (COPILOT). */
export function useAssistantConfirm() {
  return useMutation({
    mutationFn: (input: { conversationId: string; tool: string; input: Record<string, unknown> }) =>
      httpClient<AssistantConfirmResult>("/api/assistant/confirm", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
  });
}

export function useAssistantSettings(enabled = true) {
  return useQuery({
    queryKey: ["assistant", "settings", getActiveTenantId()] as const,
    queryFn: () => httpClient<AssistantSettings>("/api/assistant/settings"),
    enabled,
  });
}

export function useUpdateAssistantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Omit<AssistantSettings, "tenantId">>) =>
      httpClient<AssistantSettings>("/api/assistant/settings", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assistant", "settings"] });
      qc.invalidateQueries({ queryKey: ["assistant", "usage"] });
    },
  });
}

export function useAssistantUsage(enabled = true) {
  return useQuery({
    queryKey: ["assistant", "usage", getActiveTenantId()] as const,
    queryFn: () => httpClient<AssistantUsageStatus>("/api/assistant/usage"),
    enabled,
  });
}
